import { useHydrated } from "@trydig/ui/hooks";
import { useCallback, useLayoutEffect, useRef } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

interface SnackbarContent {
	content: string | undefined;
	variant: string;
}

interface SnackbarState {
	dismissedSnackbar: string | null;
	setDismissedSnackbar: (hash: string | null) => void;
}

const useSnackbarStore = create<SnackbarState>()(
	persist(
		(set) => ({
			dismissedSnackbar: null,
			setDismissedSnackbar: (dismissedSnackbar) => set({ dismissedSnackbar }),
		}),
		{
			name: "snackbar-storage",
			// localStorage so dismissal persists across tabs and sessions until the
			// admin publishes new content (the snackbar hash changes and we reset).
			storage: createJSONStorage(() => localStorage),
		},
	),
);

export const useSnackbar = (content: SnackbarContent) => {
	const { dismissedSnackbar, setDismissedSnackbar } = useSnackbarStore();
	const currentSnackbar = JSON.stringify({
		text: content.content,
		variant: content.variant,
	});

	const isHydrated = useHydrated();
	const prevSnackbarRef = useRef<string>(currentSnackbar);

	const isVisible = isHydrated && dismissedSnackbar !== currentSnackbar;

	const dismiss = useCallback(() => {
		setDismissedSnackbar(currentSnackbar);
	}, [currentSnackbar, setDismissedSnackbar]);

	useLayoutEffect(() => {
		if (prevSnackbarRef.current !== currentSnackbar) {
			setDismissedSnackbar(null);
			prevSnackbarRef.current = currentSnackbar;
		}
	}, [currentSnackbar, setDismissedSnackbar]);

	return {
		isVisible,
		dismiss,
	};
};
